'use client';

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';
import APP_CONFIG from '@/configs/app-config';
import { getCookieCurrency } from '@/features/currency/cookies/cookie-currency';
import { isMapViewAtom } from '@/features/listing/atoms/is-map-view-atom';
import { listingsViewAtom } from '@/features/listing/atoms/listings-view-atom';
import MapViewSwitch from '@/features/listing/components/listings-filters/map-view-switch/map-view-switch';
import ListingsListInfinity from '@/features/listing/components/listings-section/listings-list/listings-list-infinity';
import { DEFAULT_LISTING_TYPE, LISTING_URL_PART_VARIANTS } from '@/features/listing/constants/listing-constants';
import getListings from '@/features/listing/requests/get-listings';
import { PaginationNames } from '@/features/listing/types/listing-filters-types';
import { type ListingCardView, type ListingsCollection, type ListingType, type ListingWithUser } from '@/features/listing/types/listing-types';
import prepareListingsSearchParams from '@/features/listing/utils/prepare-listings-search-params';
import { getCookieCurrentPlace } from '@/features/place/cookies/cookie-current-place';
import { getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import Button from '@/ui/button/button';
import Loader from '@/ui/loader/loader';
import styles from './listings-list.module.scss';
interface ListingsListProps {
  listingsCollection: ListingsCollection;
  savedListingView?: ListingCardView | null;
  blogPostPlaceOnPage?: number | null;
}
export interface BlogPostTrigger {
  type: 'blogPost';
}
export default function ListingsInfinityScroll(props) {
  const $ = _c(43);
  const {
    listingsCollection,
    blogPostPlaceOnPage,
    savedListingView
  } = props;
  const urlSearchParams = useSearchParams();
  const pageParams = useParams();
  const t0 = savedListingView ?? APP_CONFIG.defaultListingCardView;
  let t1;
  if ($[0] !== t0) {
    t1 = [[listingsViewAtom, t0]];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useHydrateAtoms(t1);
  const isMapView = useAtomValue(isMapViewAtom);
  const listingView = useAtomValue(listingsViewAtom);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getUserBearerCookie();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const bearerTokenCookie = t2;
  const triggerNextPageLoadRef = useRef(null);
  const loadMoreObserverRef = useRef(null);
  let t3;
  if ($[3] !== urlSearchParams) {
    const pageNumberParam = new URLSearchParams(urlSearchParams).get(PaginationNames.PageNumber);
    const parsedInitialPage = pageNumberParam ? Number.parseInt(pageNumberParam, 10) : 1;
    t3 = Number.isFinite(parsedInitialPage) && parsedInitialPage > 0 ? parsedInitialPage : 1;
    $[3] = urlSearchParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const initialPage = t3;
  let t4;
  if ($[5] !== urlSearchParams) {
    const sp = new URLSearchParams(urlSearchParams);
    sp.delete(PaginationNames.PageNumber);
    const pairs = [...sp.entries()].toSorted(_temp);
    t4 = pairs.map(_temp2).join("&");
    $[5] = urlSearchParams;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const listingsParamsKey = t4;
  let t5;
  if ($[7] !== listingsParamsKey) {
    t5 = ["listings", listingsParamsKey];
    $[7] = listingsParamsKey;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const listingsQueryKey = t5;
  let t6;
  if ($[9] !== pageParams || $[10] !== urlSearchParams) {
    t6 = async t7 => {
      const {
        pageParam
      } = t7;
      const currentCurrencyNameCookie = getCookieCurrency();
      const pageListingType = pageParams.listingType ? LISTING_URL_PART_VARIANTS[pageParams.listingType as ListingType] : DEFAULT_LISTING_TYPE;
      let requestSearchParams = new URLSearchParams(urlSearchParams);
      requestSearchParams.set(PaginationNames.PageNumber, String(pageParam));
      if (!requestSearchParams.has("search_params.bookmark")) {
        const currentPlaceCookie = getCookieCurrentPlace();
        if (currentPlaceCookie) {
          requestSearchParams = prepareListingsSearchParams(requestSearchParams, pageListingType, currentPlaceCookie.bbox);
        }
      }
      const {
        items
      } = await getListings(requestSearchParams.toString(), currentCurrencyNameCookie, bearerTokenCookie);
      return items;
    };
    $[9] = pageParams;
    $[10] = urlSearchParams;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== listingsCollection.items) {
    t7 = [listingsCollection.items];
    $[12] = listingsCollection.items;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== initialPage) {
    t8 = [initialPage];
    $[14] = initialPage;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = {
      pages: t7,
      pageParams: t8
    };
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== initialPage || $[20] !== listingsQueryKey || $[21] !== t6 || $[22] !== t9) {
    t10 = {
      getNextPageParam: _temp3,
      getPreviousPageParam: _temp4,
      maxPages: 5,
      queryFn: t6,
      queryKey: listingsQueryKey,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
      initialPageParam: initialPage,
      initialData: t9
    };
    $[19] = initialPage;
    $[20] = listingsQueryKey;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const query = useInfiniteQuery(t10);
  let t11;
  if ($[24] !== initialPage || $[25] !== query) {
    t11 = () => {
      if (initialPage <= 1 || !query.data) {
        return;
      }
      if (query.hasPreviousPage && !query.isFetchingPreviousPage) {
        const currentFirstPageParameter = query.data.pageParams[0];
        const anchorElement = currentFirstPageParameter === undefined ? null : document.querySelector(`[data-page="${currentFirstPageParameter}"]`);
        const previousTop = anchorElement?.getBoundingClientRect().top ?? null;
        query.fetchPreviousPage().then(() => {
          if (!anchorElement || previousTop === null) {
            return;
          }
          requestAnimationFrame(() => {
            const newTop = anchorElement.getBoundingClientRect().top;
            const delta = newTop - previousTop;
            if (delta !== 0) {
              window.scrollBy({
                behavior: "auto",
                left: 0,
                top: delta
              });
            }
          });
        }).catch(_temp5);
      }
    };
    $[24] = initialPage;
    $[25] = query;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const loadPreviousPage = t11;
  let t12;
  let t13;
  if ($[27] !== query) {
    t12 = () => {
      const target = triggerNextPageLoadRef.current;
      if (!target) {
        return;
      }
      const observerCallback = entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !query.isFetchingNextPage && query.hasNextPage) {
            query.fetchNextPage().catch(_temp6);
          }
        });
      };
      if (!loadMoreObserverRef.current) {
        loadMoreObserverRef.current = new IntersectionObserver(observerCallback, {
          root: null,
          rootMargin: "200px",
          threshold: 1
        });
      }
      loadMoreObserverRef.current.observe(target);
      return () => {
        if (loadMoreObserverRef.current) {
          loadMoreObserverRef.current.disconnect();
        }
        loadMoreObserverRef.current = null;
      };
    };
    t13 = [query];
    $[27] = query;
    $[28] = t12;
    $[29] = t13;
  } else {
    t12 = $[28];
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] !== blogPostPlaceOnPage || $[31] !== isMapView || $[32] !== listingView || $[33] !== loadPreviousPage || $[34] !== query) {
    t14 = !isMapView && !!query.data && query.data.pages.length > 0 && <>{query.hasPreviousPage && <div className={styles.listingsList_loadMoreTrigger}><Button element="button" onClick={loadPreviousPage} isLoading={query.isFetchingPreviousPage} variant="outlined">Load previous page</Button></div>}{query.data.pages.map((page, pageIndex) => {
        const cardsData = blogPostPlaceOnPage ? [...page] : page;
        if (blogPostPlaceOnPage) {
          cardsData.splice(blogPostPlaceOnPage, 0, {
            type: "blogPost"
          });
        }
        return <ListingsListInfinity key={query.data.pageParams[pageIndex] as number} listingsList={cardsData} listingView={listingView} pageNumber={query.data.pageParams[pageIndex] as number} />;
      })}</>;
    $[30] = blogPostPlaceOnPage;
    $[31] = isMapView;
    $[32] = listingView;
    $[33] = loadPreviousPage;
    $[34] = query;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className={styles.listingsList_mapViewSwitch}><MapViewSwitch /></div>;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== isMapView || $[38] !== query) {
    t16 = !isMapView && <><div ref={triggerNextPageLoadRef} className={styles.listingsList_loadMoreTrigger} />{query.isFetchingNextPage && <div className={styles.listingsList_loader}><Loader size={12} /></div>}</>;
    $[37] = isMapView;
    $[38] = query;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t14 || $[41] !== t16) {
    t17 = <div>{t14}{t15}{t16}</div>;
    $[40] = t14;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
}
function _temp6() {
  return null;
}
function _temp5() {
  return null;
}
function _temp4(_firstPage, _pages_0, firstPageParameter) {
  const previous = firstPageParameter - 1;
  return previous >= 1 ? previous : undefined;
}
function _temp3(lastPage, _pages, lastPageParameter) {
  return lastPage.length > 0 ? lastPageParameter + 1 : undefined;
}
function _temp2(t0) {
  const [k, v] = t0;
  return `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
}
function _temp(t0, t1) {
  const [aKey, aValue] = t0;
  const [bKey, bValue] = t1;
  if (aKey === bKey) {
    return aValue.localeCompare(bValue);
  }
  return aKey.localeCompare(bKey);
}